// @ts-nocheck
import _ from 'lodash';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { makeStyles, Box, Tab, Tabs, Icon } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  useAlert,
  useHistoryState,
  useIfMounted,
  useOnMount,
  useOnPendingActionFinish,
} from '../../../../common/hooks';
import { AlertSeverity } from '../../../../common/providers';
import { AsyncUtils } from '../../../../common/utils';
import { LoadingWrapper, Panel, TabPanel } from '../../../../common/components';
import { fetchCustomer, fetchRegions } from '../../state/customerActions';
import {
  CHANGE_SUBSCRIPTION,
  CLOSE_SUBSCRIPTION,
  CREATE_SUPPORT_PRODUCT,
  fetchChannels,
  fetchProducts,
  fetchSubscriptions,
  REACTIVATE_SUBSCRIPTION,
} from '../../../subscriptions';
import { CANCEL_ORDER, fetchOrders, REFUND_ORDER } from '../../../orders';
import { fetchEvents, fetchCorrespondence } from '../../../events';
import { selectPendingActions } from '../../../../common/state/pending';
import CustomerIdentityPage from '../identity/CustomerIdentityPage';
import SubscriptionsPage from '../../../subscriptions/components/SubscriptionsPage';
import OrdersPage from '../../../orders/components/OrdersPage';
import ActivitiesPage from '../../../activities/components/ActivitiesPage';
import DevicesPage from '../../../devices/components/DevicesPage';
import EventsPage from '../../../events/components/EventsPage';
import TracersPage from '../../../tracers/components/TracersPage';
import TapsPage from '../../../taps/components/TapsPage';
import { selectCustomerDetails } from '../../state/customerSelectors';
import { UPDATE_CUSTOMER } from '../../state/customerActionTypes';
import HouseholdPage from '../../../household/components/HouseholdPage';

const TabIndex = {
  Identity: 0,
  Subscriptions: 1,
  Orders: 2,
  Activities: 3,
  Household: 4,
  Devices: 5,
  Events: 6,
  Tracers: 7,
  Taps: 8,
};

const useStyles = makeStyles(theme => ({
  root: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
  },
  content: {
    flex: '1',
    height: '100%',
    marginTop: theme.spacing(3),
  },
  tabs: {
    '& .MuiTab-root': {
      [theme.breakpoints.up('sm')]: {
        minWidth: 150,
      },
    },
    '& .MuiTab-root.Mui-selected': {
      backgroundColor: `${theme.palette.primary.main}15`,
      border: `1px solid ${theme.palette.primary.main}`,
      borderRadius: theme.shape.borderRadius,
    },
    '& .MuiTabs-indicator': {
      display: 'none',
    },
  },
}));

function CustomerDetailsPage() {
  const [loading, setLoading] = useHistoryState('loading', true);
  const [tabIndex, setTabIndex] = useHistoryState('tabIndex', TabIndex.Identity);
  const queryClient = useQueryClient();
  const [t] = useTranslation();
  const { userId } = useParams();
  const { showAlert } = useAlert();
  const customerDetails = useSelector(selectCustomerDetails);
  const pendingActions = useSelector(selectPendingActions);
  const classes = useStyles();
  const dispatch = useDispatch();
  const ifMounted = useIfMounted();

  useOnMount(() => {
    async function handleFetchData() {
      try {
        if (!customerDetails) {
          setLoading(true);
        }
        await AsyncUtils.executeAll([
          dispatch(fetchCustomer(userId)),
          dispatch(fetchSubscriptions(userId)),
          dispatch(fetchOrders(userId)),
          // dispatch(fetchDevices(userId)),
          dispatch(fetchEvents(userId)),
          dispatch(fetchCorrespondence(userId)),
          dispatch(fetchRegions()),
          dispatch(fetchChannels()),
          dispatch(fetchProducts()),
        ]);
      } catch (e) {
        showAlert({
          title: t('customer.customerDetailsError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      } finally {
        ifMounted(() => setLoading(false));
      }
    }
    handleFetchData();
  });

  const handleFetchEvents = useCallback(async () => {
    try {
      await dispatch(fetchEvents(userId));
      queryClient.invalidateQueries('eventEmails');
    } catch (e) {
      showAlert({
        title: t('event.eventsFetchError'),
        message: e.message,
        severity: AlertSeverity.Error,
      });
    }
  }, [dispatch, queryClient, showAlert, t, userId]);

  useOnPendingActionFinish(
    [
      CHANGE_SUBSCRIPTION,
      CLOSE_SUBSCRIPTION,
      REACTIVATE_SUBSCRIPTION,
      CREATE_SUPPORT_PRODUCT,
      REFUND_ORDER,
      CANCEL_ORDER,
      UPDATE_CUSTOMER,
    ],
    () => {
      handleFetchEvents();
    }
  );

  const isTabDisabled = index => !_.isEmpty(pendingActions) && index !== tabIndex;

  return (
    <Box className={classes.root}>
      <LoadingWrapper loading={loading}>
        <Panel id="CustomerDetails-tabsPanel" title={t('customer.customerDetailsTitle')}>
          <Tabs
            id="CustomerDetails-tabs"
            className={classes.tabs}
            data-value={tabIndex}
            value={tabIndex}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            onChange={(_event, index) => setTabIndex(index)}
          >
            <Tab
              id="CustomerDetails-identityTab"
              label={t('customer.customerDetailsIdentity')}
              icon={<Icon>perm_identity</Icon>}
              data-index={TabIndex.Identity}
              disabled={isTabDisabled(TabIndex.Identity)}
            />
            <Tab
              id="CustomerDetails-subscriptionsTab"
              label={t('customer.customerDetailsSubscriptions')}
              icon={<Icon>subscriptions</Icon>}
              data-index={TabIndex.Subscriptions}
              disabled={isTabDisabled(TabIndex.Subscriptions)}
            />
            <Tab
              id="CustomerDetails-ordersTab"
              label={t('customer.customerDetailsOrders')}
              icon={<Icon>receipt_long</Icon>}
              data-index={TabIndex.Orders}
              disabled={isTabDisabled(TabIndex.Orders)}
            />
            <Tab
              id="CustomerDetails-activitiesTab"
              label={t('customer.customerDetailsActivities')}
              icon={<Icon>play_arrow</Icon>}
              data-index={TabIndex.Activities}
              disabled={isTabDisabled(TabIndex.Activities)}
            />
            <Tab
              id="CustomerDetails-householdTab"
              label={t('customer.customerDetailsHousehold')}
              icon={<Icon>house</Icon>}
              data-index={TabIndex.Household}
              disabled={isTabDisabled(TabIndex.Household)}
            />
            <Tab
              id="CustomerDetails-devicesTab"
              label={t('customer.customerDetailsDevices')}
              icon={<Icon>devices</Icon>}
              data-index={TabIndex.Devices}
              disabled={isTabDisabled(TabIndex.Devices)}
            />
            <Tab
              id="CustomerDetails-eventsTab"
              label={t('customer.customerDetailsEvents')}
              icon={<Icon>assignment</Icon>}
              data-index={TabIndex.Events}
              disabled={isTabDisabled(TabIndex.Events)}
            />
            <Tab
              id="CustomerDetails-tracersTab"
              label={t('customer.customerDetailsTracers')}
              icon={<Icon>vpn_key</Icon>}
              data-index={TabIndex.Tracers}
              disabled={isTabDisabled(TabIndex.Tracers)}
            />
            <Tab
              id="CustomerDetails-tapsTab"
              label={t('customer.customerDetailsTaps')}
              icon={<Icon>checkbox</Icon>}
              data-index={TabIndex.Taps}
              disabled={isTabDisabled(TabIndex.Taps)}
            />
          </Tabs>
        </Panel>
        <Box id="CustomerDetails-tabsContent" className={classes.content}>
          <TabPanel index={TabIndex.Identity} currentIndex={tabIndex}>
            <CustomerIdentityPage />
          </TabPanel>
          <TabPanel index={TabIndex.Subscriptions} currentIndex={tabIndex}>
            <SubscriptionsPage />
          </TabPanel>
          <TabPanel index={TabIndex.Orders} currentIndex={tabIndex}>
            <OrdersPage />
          </TabPanel>
          <TabPanel index={TabIndex.Activities} currentIndex={tabIndex}>
            <ActivitiesPage />
          </TabPanel>
          <TabPanel index={TabIndex.Household} currentIndex={tabIndex}>
            <HouseholdPage />
          </TabPanel>
          <TabPanel index={TabIndex.Devices} currentIndex={tabIndex}>
            <DevicesPage />
          </TabPanel>
          <TabPanel index={TabIndex.Events} currentIndex={tabIndex}>
            {tabIndex === TabIndex.Events ? <EventsPage /> : null}
          </TabPanel>
          <TabPanel index={TabIndex.Tracers} currentIndex={tabIndex}>
            <TracersPage />
          </TabPanel>
          <TabPanel index={TabIndex.Taps} currentIndex={tabIndex}>
            <TapsPage />
          </TabPanel>
        </Box>
      </LoadingWrapper>
    </Box>
  );
}

export default CustomerDetailsPage;
